// @ts-strict-ignore
import React, { useContext } from 'react';
import type { RuleBuilderRule } from 'flow/service/rules';
import {
  findRule,
  getLocalizedIndex,
  getPathToParent,
} from 'flow/service/rules';
import { useAppDispatch } from '~/store/reduxHooks';
import { RuleBuilderContext } from '../../../context';
import {
  StyledPendingOpeningParenthesisContainer,
  StyledPendingOpeningParenthesisDropdown,
} from './styles';
import { newParenthesisOptions, pendingParenthesisOptions } from '../../Group/constants';
import { setRuleBuilderPendingGroupInfo, createWorkflowOperationMessage } from 'flow/store/redux/slice';
import { resetRuleBuilderPendingGroupStateThunk } from 'flow/store/redux/thunks';
import RuleMultiSelect from '../RuleMultiSelect';
import { useUniqueId } from '@usealloy/component-library';
import { v4 as uuidv4 } from 'uuid';
import { OperationMessageType } from 'flow/store/engine/types';
import { RuleUpdateOperationType } from 'flow/store/engine/update/constants';

type PendingOpenGroupProps = {
  rule: RuleBuilderRule;
  hasPendingOpenParenthesis: boolean;
};

export default function PendingOpenGroupDropdown({
  rule, hasPendingOpenParenthesis,
}: PendingOpenGroupProps) {
  const dispatch = useAppDispatch();
  const { sourceAction, keyboardFocus, ruleTree } = useContext(RuleBuilderContext);
  const id = useUniqueId('open-group-multiselect');

  const onOpenGroup = () => {
    const parentRulePath = getPathToParent(rule.data.path);
    const parentRule = findRule(ruleTree, { rulePath: parentRulePath });

    dispatch(
      setRuleBuilderPendingGroupInfo({
        parentRuleId: parentRule.id,
        startIndex: getLocalizedIndex(rule.data.path),
      }),
    );

    // Show assistance message for pending group
    dispatch(createWorkflowOperationMessage({
      object: {
        id: uuidv4(),
        publicId: sourceAction.publicId,
        type: OperationMessageType.actionRuleThreshold,
        timestamp: Date.now(),
        message: RuleUpdateOperationType.CANCEL_PENDING_RULE_GROUP,
        data: undefined,
      },
    }));
  };

  const onChange = option => {
    if (option.value === 'open') {
      onOpenGroup();
    } else if (option.value === 'cancel') {
      dispatch(resetRuleBuilderPendingGroupStateThunk(sourceAction.publicId));
    }
    // TODO(BUILD-3595): figure out why this id keeps getting reset when we open pending groups
    setTimeout(() => keyboardFocus.setFocusedElement(id), 50);
  };

  return (
    <StyledPendingOpeningParenthesisContainer hasPendingOpenParenthesis={hasPendingOpenParenthesis}>
      <StyledPendingOpeningParenthesisDropdown
        hasPendingOpenParenthesis={hasPendingOpenParenthesis}
      >
        <RuleMultiSelect
          options={hasPendingOpenParenthesis ? pendingParenthesisOptions : newParenthesisOptions}
          onChange={onChange}
          openMenuOnFocus
          placeholder={hasPendingOpenParenthesis ? '(' : ''}
          selectedOptions={null}
          id={id}
          isMulti={false}
          isNaked
        />
      </StyledPendingOpeningParenthesisDropdown>
    </StyledPendingOpeningParenthesisContainer>
  );
}
